import platform, { Product, analytics } from '@play-co/gcinstant';
import StateObserver from 'src/StateObserver';
import i18n from '../i18n/i18n';
import {
  trackPurchaseFailure,
  trackPurchaseSuccess,
  trackPurchaseTapped,
  trackRevenue,
} from 'src/lib/analytics/events';
import { State } from 'src/state';
import { devSettings } from 'src/lib/settings';
import { setPurchaseTime } from 'src/state/analytics';
import {
  isPlatformGuest,
  serverSyncedLocalTime,
  withLoading,
} from 'src/lib/utils';
import { Iap, ProductId } from '../../replicant/iap';
import { openPopupPromise } from '../popups/popupOpenClose';
import { Inventory } from '../../replicant/buruburu/inventory';
import { realMoneyPriceToReadableString } from './iapConversion';
import { iapClient } from './iapClient';

// Import game catalog to platform, ONLY for local development!!!
export function importProductsCatalogToPlatform() {
  if (!process.env.REPLICANT_OFFLINE || !devSettings.get('enableIAP')) return;

  platform.catalog = [];

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });
  const { ruleset } = Iap;
  for (let index = 0; index < ruleset.productIds.length; index++) {
    const productId = ruleset.productIds[index];
    const price = ruleset.prices[productId];
    const icon = '';

    platform.catalog[index] = {
      title: productId,
      id: productId,
      description: productId,
      imageURI: icon,
      price: formatter.format(price / 100),
      currencyCode: 'usd',
    };
  }
}

export const arePaymentsAvailable = ({ iap }: State) =>
  iap.ready && !isPlatformGuest(platform);

function usingTestProducts() {
  return (
    platform.catalog.length > 0 && platform.catalog[0].id.includes('.test')
  );
}

export function getProductDataById(productId: ProductId, catalog?: Product[]) {
  if (!catalog) catalog = platform.catalog;
  const product = catalog.find((x) => x.id === productId);
  if (!product) {
    throw new Error(`Product ${productId} does not exist!`);
  }

  const rewards = Iap.api.getProductRewards(productId);
  const price = realMoneyPriceToReadableString(
    product.price,
    product.currencyCode,
  );

  return {
    ...product,
    id: productId,
    rewards,
    price,
  };
}

export const convertPriceToNumber = (price: string): number =>
  +price.replace(/[^0-9.,]+/g, '');

// Check if product is in the runtime platform catalog
export const isProductAvailable = (productId: ProductId) =>
  platform.catalog.some((x) => x.id === productId);

export async function buyProduct(opts: {
  productId: ProductId;
  payload?: string;
  feature: string;
  subFeature: string;
}): Promise<void> {
  if (isPlatformGuest(platform)) {
    await openPopupPromise('popupNativeLineGuestLogin', { origin: 'feature' });
    return;
  }

  const { productId } = opts;
  const product = getProductDataById(productId);

  if (!product) {
    throw Error(`Product ${productId} does not exist in catalog!`);
  }

  console.log(`Buying ${productId} for ${product.price}`);

  const tappedTime = Date.now();
  StateObserver.dispatch(setPurchaseTime({ timeNow: serverSyncedLocalTime() }));

  const revenueOpts = {
    revenueGross: Iap.ruleset.prices[productId] / 100,
    productID: opts.productId,
    feature: opts.feature,
    subFeature: opts.subFeature,
  };

  trackPurchaseTapped(revenueOpts);

  const { user } = StateObserver.getState();
  const beforeItems = Inventory.getters(user).getCurrencies(tappedTime);
  const rewardTracking = {
    paidGems: product.rewards.paidGems || 0,
    freeGems: product.rewards.freeGems || 0,
  };

  try {
    const { purchase, product } = await withLoading(() =>
      platform.purchaseAsync(productId, opts.payload || ''),
    );

    const newUser = StateObserver.getState().user;
    const afterItems = Inventory.getters(newUser).getCurrencies(tappedTime);
    void openPopupPromise('popupPurchaseComplete', {
      old: beforeItems,
      new: afterItems,
    });

    trackRevenue({
      revenueType: 'in_app_purchase',
      currencyCodeLocal: purchase.currency || product.currencyCode,
      dollarToLocalRate: purchase.dollarToLocalRate,
      revenueGrossLocal: purchase.revenueGrossLocal,
      ...revenueOpts,
      ...rewardTracking,

      // Override ruleset product price with the USD revenue reported by GCInstant:
      revenueGross: purchase.revenueGrossUSD ?? revenueOpts.revenueGross,
    });

    const timePurchaseSuccess = Date.now();
    trackPurchaseSuccess({
      elapsedSeconds: (timePurchaseSuccess - tappedTime) / 1000,
      beforeFreeGems: beforeItems.freeGems,
      beforePaidGems: beforeItems.paidGems,
      afterFreeGems: afterItems.freeGems,
      afterPaidGems: afterItems.paidGems,
      ...revenueOpts,
    });
  } catch (err) {
    const userCancelled =
      err.code === 'USER_INPUT' || err.code === 'paymentCancelled';

    trackPurchaseFailure({
      elapsedSeconds: (Date.now() - tappedTime) / 1000,
      userCancelled,
      errorMessage: err.message,
      errorCode: err.code,
      ...revenueOpts,
    });

    // Ignore user canceling the purchase dialog.
    if (userCancelled) {
      return;
    }

    void openPopupPromise('popupError', {
      title: i18n('error.general.title'),
      message: err.message,
    });
  }
}

/**
 * Convert cents to dollars: 99 -> 0.99
 */
function centsToDollars(cents: number) {
  return Number((cents / 100).toFixed(2));
}

export async function buyProductStripe(opts: {
  productId: ProductId;
  payload?: string;
  feature: string;
  subFeature: string;
}): Promise<boolean> {
  await iapClient.iapInit();

  const { productId } = opts;
  const product = getProductDataById(productId, iapClient.catalog);

  if (!product) {
    throw Error(`Product ${productId} does not exist in catalog!`);
  }

  console.log(`Buying ${productId} for ${product.price}`);

  const tappedTime = Date.now();
  StateObserver.dispatch(setPurchaseTime({ timeNow: serverSyncedLocalTime() }));

  const revenueOpts = {
    revenueGross: centsToDollars(Iap.ruleset.prices[productId]),
    productID: opts.productId,
    feature: opts.feature,
    subFeature: opts.subFeature,
  };

  trackPurchaseTapped(revenueOpts);

  const { user } = StateObserver.getState();
  const beforeItems = Inventory.getters(user).getCurrencies(tappedTime);
  const rewardTracking = {
    paidGems: product.rewards.paidGems || 0,
    freeGems: product.rewards.freeGems || 0,
  };

  try {
    const { purchase, product } = await withLoading(() =>
      iapClient.purchaseAsync(productId, opts.payload || ''),
    );

    const newUser = StateObserver.getState().user;
    const afterItems = Inventory.getters(newUser).getCurrencies(tappedTime);
    void openPopupPromise('popupPurchaseComplete', {
      old: beforeItems,
      new: afterItems,
    });

    const revenueGross = purchase.revenueGrossUSD
      ? centsToDollars(purchase.revenueGrossUSD)
      : revenueOpts.revenueGross;

    const revenueGrossLocal = centsToDollars(purchase.revenueGrossLocal);

    // Converting revenueGross and revenueGrossLocal from cents to dollars: 99 -> 0.99
    trackRevenue({
      revenueType: 'in_app_purchase',
      currencyCodeLocal: purchase.currency || product.currencyCode,
      dollarToLocalRate: purchase.dollarToLocalRate,
      revenueGrossLocal,
      ...revenueOpts,
      ...rewardTracking,
      revenueGross,
    });

    const timePurchaseSuccess = Date.now();
    trackPurchaseSuccess({
      elapsedSeconds: (timePurchaseSuccess - tappedTime) / 1000,
      beforeFreeGems: beforeItems.freeGems,
      beforePaidGems: beforeItems.paidGems,
      afterFreeGems: afterItems.freeGems,
      afterPaidGems: afterItems.paidGems,
      ...revenueOpts,
    });

    // Return true if user has purchased anything
    return true;
  } catch (err) {
    const userCancelled =
      err.code === 'USER_INPUT' || err.code === 'paymentCancelled';

    trackPurchaseFailure({
      elapsedSeconds: (Date.now() - tappedTime) / 1000,
      userCancelled,
      errorMessage: err.message,
      errorCode: err.code,
      ...revenueOpts,
    });

    // Ignore user canceling the purchase dialog.
    if (userCancelled) {
      return false;
    }

    void openPopupPromise('popupError', {
      title: i18n('error.general.title'),
      message: err.message,
    });

    return false;
  }
}

export async function checkPendingGemsPurchase() {
  const result = await iapClient.checkPendingPayments();

  if (!result) return;

  analytics.pushEvent('PendingPurchaseRedirect');

  switch (result.status) {
    case 'succeeded':
      analytics.pushEvent('PendingPurchaseRedirectSuccess');
      await completePendingGemsPurchase(result.productId);
      break;
    case 'processing':
      analytics.pushEvent('PendingPurchaseRedirectFailed', {
        status: result.status,
      });
      await openPopupPromise('popupConfirmation', {
        title: 'Payment Status',
        message: 'Your payment is processing.',
      });
      break;
    case 'requires_payment_method':
      analytics.pushEvent('PendingPurchaseRedirectFailed', {
        status: result.status,
      });
      await openPopupPromise('popupConfirmation', {
        title: 'Payment Failed',
        message: 'Your payment was not successful, please try again.',
      });
      break;
    default:
      analytics.pushEvent('PendingPurchaseRedirectFailed', {
        status: result.status,
      });
      await openPopupPromise('popupConfirmation', {
        title: 'Payment Failed',
        message: 'Something went wrong. Try again',
      });
      break;
  }
}

async function completePendingGemsPurchase(productId: string) {
  const rewards = Iap.ruleset.gemsRewardTable[productId];
  if (!rewards) return;
  const freeGems = rewards.freeGems ?? 0;
  const paidGems = rewards.paidGems ?? 0;
  const { user } = StateObserver.getState();
  const itemsBefore = Inventory.getters(user).getCurrencies(Date.now());
  const itemsAfter = {
    ...itemsBefore,
    freeGems: itemsBefore.freeGems + freeGems,
    paidGems: itemsBefore.paidGems + paidGems,
  };
  await iapClient.completePurchase(productId);
  await openPopupPromise('popupPurchaseComplete', {
    old: itemsBefore,
    new: itemsAfter,
  });
}
